import { SkipNavTarget } from "@modules/a11y"
import { FadeOnMount } from "@modules/animation"
import { logout, ssoLogout } from "@modules/auth"
import { useTrackSentry } from "@modules/auth/hooks/useTrackSentry"
import { useCurrentUserQuery } from "@modules/auth/queries.generated"
import { setIdentify } from "@modules/fullStory"
import { LocalStorageItems } from "@modules/localStorage/constants"
import { useMaintenanceMode } from "@modules/serviceRoutes/utils"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import Loading from "@modules/ui/components/Loading"
import { StandardSingleColumn } from "@modules/ui/layouts/Containers"
import { Redirect } from "@gatsbyjs/reach-router"
import { isAfter } from "date-fns"
import { Spacer } from "gatsby-interface"
import PropTypes from "prop-types"
import React from "react"
import { useSecureWorkspace } from "../hooks/useSecureWorkspace"

function ProtectedRoute({ location, ...rest }) {
  const organizationId = rest?.organizationId

  // checks the workspacess access setup
  const workspace = useSecureWorkspace(organizationId, {
    skip: !organizationId,
  })

  if (workspace?.loading) {
    return <Loading delay={1000} message="authenticating ..." />
  }

  const tokenExpiration = localStorage.getItem(
    LocalStorageItems.GatsbyTokenExpiration
  )

  const shouldLogoutUser =
    !tokenExpiration || isAfter(new Date(), new Date(tokenExpiration))

  const loginRedirectUrl = `${location.pathname}${location.search || ``}`

  // defines the logout method based on the workspace setup (has or not SSO integration)
  const doLogout = workspace?.hasSsoIntegration ? ssoLogout : logout

  // if there is no token expiration, the token has expired
  // or the user is forbiden to interact with workspace,
  // it logs the user out
  if (shouldLogoutUser || workspace?.isReadyToInteract === false) {
    doLogout(rest.uri ? `?loginRedirectUrl=${loginRedirectUrl}` : ``)

    return null
  }

  if (!localStorage.getItem(LocalStorageItems.GatsbyToken)) {
    // If we’re not logged in, redirect to the home page.

    if (location.pathname && location.pathname.includes("deploynow")) {
      // exceptional case for deploynow flow
      return (
        <Redirect
          to={`/dashboard/signup?loginRedirectUrl=${loginRedirectUrl}`}
          noThrow
        />
      )
    }

    return (
      <Redirect
        to={`/dashboard/login?loginRedirectUrl=${loginRedirectUrl}`}
        noThrow
      />
    )
  }

  return <AuthenticatedRoute {...rest} location={location} />
}

function AuthenticatedRoute({ location, ...rest }) {
  const { data, loading, error } = useCurrentUserQuery()

  useMaintenanceMode({ error })

  const currentUser = data?.currentUser
  useTrackSentry(currentUser)

  if (loading && !currentUser) {
    return (
      <FadeOnMount delay={1000}>
        <Loading delay={1000} message="authenticating ..." />
      </FadeOnMount>
    )
  }

  if (error) {
    return (
      <main>
        <SkipNavTarget />
        <StandardSingleColumn>
          <Spacer size={10} />
          <ErrorAlert>{error.message}</ErrorAlert>
        </StandardSingleColumn>
      </main>
    )
  }

  if (!currentUser) {
    return <Redirect to="/dashboard/logout" noThrow />
  }

  // Call identify API from fullstory
  if (location.search.includes(`loggedin=1`)) {
    setIdentify(currentUser)
  }

  if (currentUser.banned) {
    logout(`?error=blocked`)
    return null
  }

  const Component = rest.component

  return <Component {...rest} user={currentUser} location={location} />
}

ProtectedRoute.propTypes = {
  component: PropTypes.any.isRequired,
}

export default ProtectedRoute
